import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Pagination,
  Modal,
  Form,
  InputNumber,
  Select,
  Input,
  DatePicker,
  message,
  Alert,
} from "antd";
import dayjs from "dayjs";
import {
  fetchProcurementCreditClients,
  fetchProcurementCreditClientOrders,
  payProcurementCreditClient,
} from "../../apis/procurement.api";

const { Option } = Select;

type CreditByClientProps = {
  canManage: boolean;
  searchText: string;
  onConvertCredit: (record: any) => void;
  onRejectCredit: (record: any) => void;
  refreshToken: number;
};

type ClientRow = {
  client_id: string;
  client_name: string | null;
  client_contact: string | null;
  total_credit_amount: string | number;
  credit_count: number;
  order_count: number;
  latest_credit_at: string | null;
  oldest_credit_at: string | null;
};

const toRupees = (paise: any) => Number(paise || 0) / 100;

const formatMoney = (paise: any) =>
  `₹${toRupees(paise).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};

const CreditByClient = ({
  canManage,
  searchText,
  onConvertCredit,
  onRejectCredit,
  refreshToken,
}: CreditByClientProps) => {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [ordering, setOrdering] = useState('-total_credit_amount');

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [ordersByClient, setOrdersByClient] = useState<Record<string, any[]>>({});
  const [ordersLoading, setOrdersLoading] = useState<Record<string, boolean>>({});

  const [payClient, setPayClient] = useState<ClientRow | null>(null);
  const [paying, setPaying] = useState(false);
  const [payForm] = Form.useForm();
  const payAmount = Form.useWatch('payment_amount', payForm);

  const loadClients = async (p = page, s = searchText, o = ordering) => {
    setLoading(true);
    const res = await fetchProcurementCreditClients({ page: p, search: s, ordering: o });
    if (res) {
      setClients(res.results || []);
      setTotalItems(res.count || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadClients(page, searchText, ordering);
    // A refresh invalidates every cached accordion, otherwise an order that was
    // just paid keeps showing its old credit when the row is re-expanded.
    setOrdersByClient({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, ordering, refreshToken]);

  const loadOrders = async (clientId: string) => {
    setOrdersLoading((prev) => ({ ...prev, [clientId]: true }));
    const res = await fetchProcurementCreditClientOrders(clientId);
    if (res) {
      setOrdersByClient((prev) => ({ ...prev, [clientId]: res.orders || [] }));
    } else {
      message.error('Failed to load orders for this client');
    }
    setOrdersLoading((prev) => ({ ...prev, [clientId]: false }));
  };

  const handleExpand = (expanded: boolean, record: ClientRow) => {
    setExpandedKeys(expanded ? [...expandedKeys, record.client_id] : expandedKeys.filter((k) => k !== record.client_id));
    if (expanded && !ordersByClient[record.client_id]) {
      loadOrders(record.client_id);
    }
  };

  const openPayModal = (record: ClientRow) => {
    if (!canManage) {
      message.error("You don't have permission to authorize payments");
      return;
    }
    setPayClient(record);
    payForm.setFieldsValue({
      payment_amount: toRupees(record.total_credit_amount),
      mode: undefined,
      payment_id: undefined,
      medium: undefined,
      created_at: dayjs(),
    });
  };

  const handlePay = async () => {
    if (!payClient) return;
    let values: any;
    try {
      values = await payForm.validateFields();
    } catch {
      return;
    }

    setPaying(true);
    const res = await payProcurementCreditClient(payClient.client_id, {
      payment_amount: Math.round(Number(values.payment_amount) * 100),
      payment_method: values.mode,
      payment_medium: values.medium,
      payment_id: values.payment_id,
      created_at: values.created_at ? values.created_at.format('YYYY-MM-DD') : undefined,
    });
    setPaying(false);

    if (!res || res.error) {
      message.error(res?.error || 'Failed to record payment');
      return;
    }

    // The collection is recorded either way — the Zoho document is a separate leg
    // that the nightly backfill retries. Said plainly so nobody re-records a payment
    // that was already taken, trying to make Zoho catch up.
    if (res.zoho_synced === false) {
      message.warning(
        `${res.message}, but ${res.zoho_failed?.length || 0} order(s) have not reached Zoho yet — they will be pushed automatically tonight. No action needed.`,
        8,
      );
    } else {
      message.success(res.message);
    }

    setPayClient(null);
    setOrdersByClient((prev) => ({ ...prev, [payClient.client_id]: [] }));
    loadOrders(payClient.client_id);
    loadClients();
  };

  const clientColumns = [
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 220,
      render: (name: string, record: ClientRow) => name || `Client #${record.client_id}`,
    },
    {
      title: 'Contact',
      dataIndex: 'client_contact',
      key: 'client_contact',
      width: 140,
      render: (v: string) => v || '-',
    },
    {
      title: 'Orders',
      dataIndex: 'order_count',
      key: 'order_count',
      width: 90,
      align: 'right' as const,
      sorter: true,
    },
    {
      title: 'Entries',
      dataIndex: 'credit_count',
      key: 'credit_count',
      width: 90,
      align: 'right' as const,
      sorter: true,
    },
    {
      title: 'Total Credit',
      dataIndex: 'total_credit_amount',
      key: 'total_credit_amount',
      width: 150,
      align: 'right' as const,
      defaultSortOrder: 'descend' as const,
      sorter: true,
      render: (amount: any) => <strong>{formatMoney(amount)}</strong>,
    },
    {
      title: 'Oldest Credit',
      dataIndex: 'oldest_credit_at',
      key: 'oldest_credit_at',
      width: 170,
      sorter: true,
      render: (v: string) => formatDate(v),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: ClientRow) => (
        <Button type="primary" size="small" onClick={() => openPayModal(record)}>
          Convert to Payment
        </Button>
      ),
    },
  ];

  // One flat row per credit entry, carrying its order's identity. A second level of
  // nested tables inside the expanded row collided with the outer row heights and
  // buried the actions two clicks deep, so the order is a repeated column instead.
  const orderCreditRows = (clientId: string) =>
    (ordersByClient[clientId] || []).flatMap((order: any) =>
      (order.credits || []).map((credit: any) => ({
        ...credit,
        lead_id: order.lead_id,
        estimate_status: order.estimate_status,
        order_created_at: order.order_created_at,
      }))
    );

  const orderCreditColumns = [
    {
      title: 'Lead ID',
      dataIndex: 'lead_id',
      key: 'lead_id',
      width: 180,
    },
    {
      title: 'Order Date',
      dataIndex: 'order_created_at',
      key: 'order_created_at',
      width: 150,
      render: (v: string) => formatDate(v),
    },
    {
      title: 'Estimate Status',
      dataIndex: 'estimate_status',
      key: 'estimate_status',
      width: 160,
      render: (s: string) => (s ? <Tag color="geekblue">{s.replace(/_/g, ' ').toUpperCase()}</Tag> : '-'),
    },
    {
      title: 'Credit',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 120,
      align: 'right' as const,
      render: (amount: any) => formatMoney(amount),
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      width: 200,
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 140,
      render: (v: string) => v || '-',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (v: string) => formatDate(v),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 210,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onConvertCredit(record)}>
            Convert
          </Button>
          <Button type="link" danger size="small" style={{ padding: 0 }} onClick={() => onRejectCredit(record)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  const outstandingRupees = payClient ? toRupees(payClient.total_credit_amount) : 0;

  return (
    <>
      <Table
        columns={clientColumns}
        dataSource={clients}
        rowKey="client_id"
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpand: handleExpand,
          expandedRowRender: (record: ClientRow) => (
            <div style={{ padding: '4px 0 8px' }}>
              <Table
                columns={orderCreditColumns}
                dataSource={orderCreditRows(record.client_id)}
                rowKey="id"
                size="small"
                bordered
                pagination={false}
                loading={ordersLoading[record.client_id]}
                scroll={{ x: 'max-content' }}
              />
            </div>
          ),
        }}
        onChange={(_p, _f, sorter: any) => {
          const field = sorter?.field;
          if (!field || !sorter.order) {
            setOrdering('-total_credit_amount');
          } else {
            setOrdering(sorter.order === 'ascend' ? field : `-${field}`);
          }
          setPage(1);
        }}
      />

      <Pagination
        current={page}
        total={totalItems}
        pageSize={10}
        showSizeChanger={false}
        className="mt-3 text-end"
        onChange={(p) => setPage(p)}
      />

      <Modal
        title={`Convert Credit to Payment — ${payClient?.client_name || ''}`}
        open={Boolean(payClient)}
        onCancel={() => setPayClient(null)}
        onOk={handlePay}
        okText="Convert to Payment"
        okButtonProps={{ loading: paying }}
        cancelButtonProps={{ disabled: paying }}
      >
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={`Outstanding credit: ${formatMoney(payClient?.total_credit_amount)} across ${payClient?.order_count || 0} order(s)`}
          description="The amount is applied to the client's latest order first and works backwards. If it runs out mid-order, the remainder stays as credit on that order."
        />
        <Form form={payForm} layout="vertical">
          <Form.Item
            name="payment_amount"
            label="Amount Received"
            rules={[
              { required: true, message: 'Please enter amount' },
              {
                validator: (_: any, value: any) =>
                  !value || Number(value) <= outstandingRupees
                    ? Promise.resolve()
                    : Promise.reject(new Error(`Cannot exceed the outstanding credit (₹${outstandingRupees.toLocaleString('en-IN')})`)),
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="Enter amount" />
          </Form.Item>

          <Form.Item name="mode" label="Payment Mode" rules={[{ required: true, message: 'Please select payment mode' }]}>
            <Select placeholder="Select payment mode">
              <Option value="neft_rtgs_imps">NEFT / RTGS / IMPS</Option>
              <Option value="upi">UPI</Option>
              <Option value="cash">Cash</Option>
              <Option value="card">Card</Option>
              <Option value="cheque">Cheque</Option>
              <Option value="razorpay">Razorpay</Option>
            </Select>
          </Form.Item>

          <Form.Item name="payment_id" label="Payment ID" rules={[{ required: true, message: 'Please enter Payment ID' }]}>
            <Input placeholder="Enter Payment ID" />
          </Form.Item>

          <Form.Item name="medium" label="Payment Medium">
            <Select placeholder="Select payment medium" allowClear>
              <Option value="pinelabs">PineLabs</Option>
              <Option value="razorpay">Razorpay</Option>
              <Option value="razorpay_pos">Razorpay POS</Option>
            </Select>
          </Form.Item>

          <Form.Item name="created_at" label="Payment Creation Date" rules={[{ required: true, message: 'Please select payment creation date' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
        {payAmount && Number(payAmount) < outstandingRupees ? (
          <Alert
            type="warning"
            showIcon
            message={`₹${(outstandingRupees - Number(payAmount)).toLocaleString('en-IN')} will remain as credit for this client.`}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default CreditByClient;
